'use client'

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { StorageAllocation } from '@garage/shared'
import { PageHeader, Button, EmptyState, Skeleton, ConfirmDialog, AddIcon, InventoryIcon, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { ActivityInfo } from '@/components/ActivityInfo.js'
import { AllocationFormDialog } from '@/components/storage/AllocationFormDialog.js'
import { AllocationDetailDialog } from '@/components/storage/AllocationDetailDialog.js'
import { AllocationIcon } from '@/components/storage/AllocationIcon.js'
import { AllocationSortSelect, useAllocationSort } from '@/components/storage/AllocationSortSelect.js'
import { sortAllocations } from '@/components/storage/allocationSort.js'

type AllocationDialogState = { open: false } | { open: true; allocation?: StorageAllocation }
// Only the id — looked up fresh from `level.allocations` on every render, so
// an edit made while this dialog is open (like adding or removing a photo)
// shows up immediately instead of the dialog holding a stale snapshot.
type DetailState = string | null

/**
 * What a QR code on a shelf opens directly to: one level's contents, with
 * nothing else from the storage tree to navigate through first. Reads the
 * same cached tree the main Storage page uses rather than a dedicated
 * endpoint — the whole tree is already small enough to fetch in one go.
 */
export function StorageLevelPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const { data, isLoading, isError } = useQuery({ queryKey: qk.storageTree, queryFn: api.storage.tree })
  const racks = data?.items ?? []

  const found = (() => {
    for (const rack of racks) {
      const level = rack.levels.find((l) => l.id === id)
      if (level) return { rack, level }
    }
    return null
  })()

  const [allocationDialog, setAllocationDialog] = useState<AllocationDialogState>({ open: false })
  const [detail, setDetail] = useState<DetailState>(null)
  const [sort, setSort] = useAllocationSort()
  const [deleteTarget, setDeleteTarget] = useState<StorageAllocation | null>(null)

  const deleteMutation = useMutation({
    mutationFn: (allocationId: string) => api.storage.removeAllocation(allocationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.storageTree })
      snackbar.success(t('storage.removed'))
      setDeleteTarget(null)
    },
    onError: (error) => snackbar.error(error instanceof ApiError ? error.message : t('storage.failedToRemove')),
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('storage.title')} back={{ to: '/storage', label: t('storage.title') }} />
        <Skeleton lines={4} />
      </div>
    )
  }

  if (isError || !found) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('storage.title')} back={{ to: '/storage', label: t('storage.title') }} />
        <EmptyState
          icon={<InventoryIcon className="w-12 h-12 text-on-surface-variant" />}
          title={t('storage.levelNotFound')}
          description={t('storage.levelMayHaveBeenRemoved')}
        />
      </div>
    )
  }

  const { rack, level } = found
  const detailAllocation = detail ? (level.allocations.find((a) => a.id === detail) ?? null) : null

  return (
    <div className="space-y-6">
      <PageHeader
        title={level.name}
        subtitle={rack.name}
        back={{ to: '/storage', label: t('storage.title') }}
        actions={
          <Button
            variant="filled"
            icon={<AddIcon className="w-5 h-5" />}
            onClick={() => setAllocationDialog({ open: true })}
          >
            {t('storage.addItem')}
          </Button>
        }
      />

      <ActivityInfo entityType="storage_node" entityId={level.id} />

      {level.description && <p className="text-body-md text-on-surface-variant">{level.description}</p>}

      {level.allocations.length === 0 ? (
        <EmptyState
          icon={<InventoryIcon className="w-12 h-12 text-on-surface-variant" />}
          title={t('storage.nothingAllocatedYet')}
          description={t('storage.addItemToStartTracking')}
          action={
            <Button variant="tonal" onClick={() => setAllocationDialog({ open: true })}>
              {t('storage.addAnItem')}
            </Button>
          }
        />
      ) : (
        <div className="bg-surface-container border border-outline-variant rounded-lg p-4">
          {level.allocations.length > 1 && (
            <div className="mb-3">
              <AllocationSortSelect value={sort} onChange={setSort} />
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {sortAllocations(level.allocations, sort).map((allocation) => (
              <AllocationIcon key={allocation.id} allocation={allocation} onClick={() => setDetail(allocation.id)} />
            ))}
          </div>
        </div>
      )}

      {allocationDialog.open && (
        <AllocationFormDialog
          open
          levelId={level.id}
          allocation={allocationDialog.allocation}
          onClose={() => setAllocationDialog({ open: false })}
        />
      )}

      <AllocationDetailDialog
        open={!!detail}
        allocation={detailAllocation}
        onClose={() => setDetail(null)}
        onEdit={() => {
          if (detailAllocation) setAllocationDialog({ open: true, allocation: detailAllocation })
          setDetail(null)
        }}
        onDelete={() => {
          if (detailAllocation) setDeleteTarget(detailAllocation)
          setDetail(null)
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('storage.removeItemTitle')}
        message={t('storage.removeItemMessage', { name: deleteTarget?.itemName })}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id)
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
